<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCloseCircleState } from 'dashboard/components-next/Contacts/CloseCircle/useCloseCircleState.js';

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  section: {
    type: String,
    default: 'client',
  },
  contactName: {
    type: String,
    default: '',
  },
  contactPhone: {
    type: String,
    default: '',
  },
  contactEmail: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['edit']);

const { t } = useI18n();
const closeCircle = useCloseCircleState();

const OCCASION_KEYS = {
  birthday: 'PURCHASE_PROJECTS.OCCASIONS.BIRTHDAY',
  wedding: 'PURCHASE_PROJECTS.OCCASIONS.WEDDING',
  gift: 'PURCHASE_PROJECTS.OCCASIONS.GIFT',
  personal: 'PURCHASE_PROJECTS.OCCASIONS.PERSONAL',
  other: 'PURCHASE_PROJECTS.OCCASIONS.OTHER',
};

const occasionLabel = computed(() =>
  props.project.occasion ? t(OCCASION_KEYS[props.project.occasion] ?? '') : null
);

const formattedDueDate = computed(() => {
  if (!props.project.dueDate) return null;
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(props.project.dueDate));
});

const beneficiaryMember = computed(() => {
  if (props.project.beneficiaryType !== 'close_circle_member') return null;
  return closeCircle.findMember(props.project.beneficiaryId);
});

const beneficiaryLabel = computed(() => {
  if (props.project.beneficiaryType !== 'close_circle_member') {
    return t('CLOSE_CIRCLE.BENEFICIARY.MAIN_CONTACT');
  }
  const member = beneficiaryMember.value;
  const snapshot = props.project.beneficiarySnapshot;
  const name =
    closeCircle.memberName(member) ||
    [snapshot?.firstName, snapshot?.lastName].filter(Boolean).join(' ');
  const relationship = member?.relationship ?? snapshot?.relationship;
  const relationLabel = relationship
    ? t(closeCircle.relationshipConfig(relationship).labelKey)
    : '';
  return relationLabel ? `${name} · ${relationLabel}` : name;
});
</script>

<template>
  <!-- Client block -->
  <div v-if="section === 'client'" class="flex flex-col">
    <div class="px-4 py-3">
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('PURCHASE_PROJECTS.DETAIL.CLIENT_SUMMARY_TITLE') }}
      </span>
    </div>
    <div class="px-4 pb-3 flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <span class="text-sm font-medium text-n-slate-12">{{
          contactName
        }}</span>
        <span
          v-if="contactPhone"
          class="flex items-center gap-1 text-xs text-n-slate-10"
        >
          <span class="i-lucide-phone size-3 shrink-0" />
          {{ contactPhone }}
        </span>
      </div>
      <span
        v-if="contactEmail"
        class="flex items-center gap-1 text-xs text-n-slate-10"
      >
        <span class="i-lucide-mail size-3 shrink-0" />
        {{ contactEmail }}
      </span>
    </div>
  </div>

  <!-- Project info block -->
  <div v-else-if="section === 'project'" class="flex flex-col">
    <div class="flex items-center justify-between px-4 py-3">
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('PURCHASE_PROJECTS.DETAIL.PROJECT_INFO_TITLE') }}
      </span>
      <button
        type="button"
        class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
        @click="emit('edit')"
      >
        <span class="i-lucide-pencil size-3.5" />
        {{ t('PURCHASE_PROJECTS.DETAIL.ACTIONS.EDIT') }}
      </button>
    </div>
    <div class="px-4 pb-3 flex flex-col gap-1.5">
      <div class="flex items-center gap-2">
        <span class="i-lucide-user size-3.5 text-n-slate-10 shrink-0" />
        <span class="text-sm text-n-slate-11">{{ project.sellerName }}</span>
      </div>
      <div v-if="occasionLabel" class="flex items-center gap-2">
        <span class="i-lucide-gift size-3.5 text-n-slate-10 shrink-0" />
        <span class="text-sm text-n-slate-11">{{ occasionLabel }}</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="i-lucide-users-round size-3.5 text-n-slate-10 shrink-0" />
        <span class="text-sm text-n-slate-11">{{ beneficiaryLabel }}</span>
      </div>
      <div v-if="formattedDueDate" class="flex items-center gap-2">
        <span class="i-lucide-calendar size-3.5 text-n-slate-10 shrink-0" />
        <span class="text-sm text-n-slate-11">{{ formattedDueDate }}</span>
      </div>
      <p
        v-if="project.internalNote"
        class="text-xs text-n-slate-10 italic leading-relaxed mt-0.5"
      >
        {{ project.internalNote }}
      </p>
    </div>
  </div>
</template>
